import { useState } from 'react';
import { useApolloClient, useMutation, gql } from '@apollo/client';
import { Button, Message, Dropdown, Icon, Segment, Checkbox, Table } from 'semantic-ui-react';

/**
 * Phase 4B.3 / 4B.4 — editing what a role grants.
 *
 * **Nothing is written until the preview has been seen.** Changing a role re-expands every
 * holder, so one row can move access for everybody who has it. The flow is deliberately two
 * steps — edit, preview, then apply — rather than a Save button that acts immediately.
 *
 * The preview comes from the server performing the change in a transaction and rolling back, so
 * what is shown here is what will happen, not a prediction of it.
 */

export interface Entry {
    resourceType: string;
    level: string;
    scoped: boolean;
    capability?: string | null;
}

const PREVIEW = gql`
  query PreviewRoleTemplate($role: String!, $entries: [RoleTemplateEntryInput!]!) {
    previewRoleTemplate(role: $role, entries: $entries) {
      role holders widensAccess capabilitiesGained capabilitiesLost
      deltas { resourceType level peopleGaining peopleLosing resourcesGained resourcesLost }
    }
  }
`;

const SET_TEMPLATE = gql`
  mutation SetRoleTemplate($role: String!, $entries: [RoleTemplateEntryInput!]!, $confirmWidening: Boolean) {
    setRoleTemplate(role: $role, entries: $entries, confirmWidening: $confirmWidening) { status }
  }
`;

interface Props {
    role: string;
    initial: Entry[];
    resourceTypes: string[];
    levels: string[];
    capabilities: string[];
    onDone: () => void;
    onCancel: () => void;
}

export const RoleEditor = ({
    role, initial, resourceTypes, levels, capabilities, onDone, onCancel,
}: Props) => {
    const client = useApolloClient();
    const [entries, setEntries] = useState<Entry[]>(initial.map((e) => ({ ...e })));
    const [preview, setPreview] = useState<any | null>(null);
    const [confirmed, setConfirmed] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [setTemplate] = useMutation(SET_TEMPLATE);

    // Any edit invalidates the preview. Without this, somebody could preview a safe change,
    // alter a row, and apply against a stale confirmation — which is the one way this flow
    // could still let a widening through unseen.
    const change = (next: Entry[]) => {
        setEntries(next);
        setPreview(null);
        setConfirmed(false);
    };

    const update = (index: number, patch: Partial<Entry>) =>
        change(entries.map((e, i) => (i === index ? { ...e, ...patch } : e)));

    const asInput = () => entries.map((e) => ({
        resourceType: e.resourceType,
        level: e.resourceType === 'CAPABILITY' ? 'VIEW' : e.level,
        scoped: e.resourceType === 'CAPABILITY' ? false : e.scoped,
        capability: e.resourceType === 'CAPABILITY' ? e.capability ?? null : null,
    }));

    const runPreview = async () => {
        setBusy(true);
        setError(null);
        try {
            const { data } = await client.query({
                query: PREVIEW,
                variables: { role, entries: asInput() },
                fetchPolicy: 'network-only',
            });
            setPreview(data.previewRoleTemplate);
        } catch (e: any) {
            setError(e?.message ?? 'Could not work out what this change would do.');
        } finally {
            setBusy(false);
        }
    };

    const apply = async () => {
        setBusy(true);
        setError(null);
        try {
            await setTemplate({
                variables: { role, entries: asInput(), confirmWidening: confirmed },
            });
            onDone();
        } catch (e: any) {
            setError(e?.message ?? 'Could not save this change.');
        } finally {
            setBusy(false);
        }
    };

    const nothingChanged = JSON.stringify(initial.map((e) => ({ ...e, capability: e.capability ?? null })))
        === JSON.stringify(entries.map((e) => ({ ...e, capability: e.capability ?? null })));

    return (
        <Segment secondary>
            <Table compact basic="very">
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell>Over</Table.HeaderCell>
                        <Table.HeaderCell>Can</Table.HeaderCell>
                        <Table.HeaderCell>Which ones</Table.HeaderCell>
                        <Table.HeaderCell />
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {entries.map((e, i) => (
                        <Table.Row key={i}>
                            <Table.Cell collapsing>
                                <Dropdown
                                    selection
                                    compact
                                    value={e.resourceType}
                                    options={resourceTypes.map((r) => ({ key: r, text: r.toLowerCase(), value: r }))}
                                    onChange={(_, d) => update(i, { resourceType: d.value as string })}
                                />
                            </Table.Cell>
                            <Table.Cell collapsing>
                                {e.resourceType === 'CAPABILITY'
                                    ? (
                                        <Dropdown
                                            selection
                                            value={e.capability ?? capabilities[0]}
                                            options={capabilities.map((c) => ({
                                                key: c, text: c.toLowerCase().replace(/_/g, ' '), value: c,
                                            }))}
                                            onChange={(_, d) => update(i, { capability: d.value as string })}
                                        />
                                    )
                                    : (
                                        <Dropdown
                                            selection
                                            compact
                                            value={e.level}
                                            options={levels.map((l) => ({ key: l, text: l.toLowerCase(), value: l }))}
                                            onChange={(_, d) => update(i, { level: d.value as string })}
                                        />
                                    )}
                            </Table.Cell>
                            <Table.Cell collapsing>
                                {e.resourceType === 'CAPABILITY'
                                    ? <span style={{ color: '#767676' }}>—</span>
                                    : (
                                        <Checkbox
                                            label={e.scoped ? 'only their own' : 'all of them'}
                                            checked={!e.scoped}
                                            onChange={(_, d) => update(i, { scoped: !d.checked })}
                                        />
                                    )}
                            </Table.Cell>
                            <Table.Cell collapsing textAlign="right">
                                <Icon
                                    name="trash"
                                    link
                                    onClick={() => change(entries.filter((_, j) => j !== i))}
                                />
                            </Table.Cell>
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table>

            <Button
                size="small"
                onClick={() => change([
                    ...entries,
                    { resourceType: resourceTypes[0], level: 'VIEW', scoped: false, capability: null },
                ])}
            >
                Add a permission
            </Button>

            {entries.length === 0 && (
                <Message size="tiny" style={{ marginTop: '1em' }}>
                    This role will grant nothing. People will keep the role, but it will give
                    them no access.
                </Message>
            )}

            {error && <Message negative size="small">{error}</Message>}

            {preview && (
                <Segment style={{ marginTop: '1em' }}>
                    <strong>
                        {preview.holders === 0
                            ? 'Nobody holds this role, so nothing changes today.'
                            : `${preview.holders} ${preview.holders === 1 ? 'person holds' : 'people hold'} this role.`}
                    </strong>

                    {preview.deltas.length === 0 && preview.capabilitiesGained.length === 0
                        && preview.capabilitiesLost.length === 0 && (
                        <p style={{ marginTop: '0.5em' }}>This change does not alter anyone's access.</p>
                    )}

                    {preview.deltas.map((d: any) => (
                        <div key={`${d.resourceType}:${d.level}`} style={{ marginTop: '0.4em' }}>
                            {d.peopleGaining > 0 && (
                                <div style={{ color: '#9f3a38' }}>
                                    <Icon name="arrow up" />
                                    {d.peopleGaining} {d.peopleGaining === 1 ? 'person gains' : 'people gain'}
                                    {' '}{d.level.toLowerCase()} on {d.resourcesGained}
                                    {' '}{d.resourceType.toLowerCase()}{d.resourcesGained === 1 ? '' : 's'}
                                </div>
                            )}
                            {d.peopleLosing > 0 && (
                                <div style={{ color: '#767676' }}>
                                    <Icon name="arrow down" />
                                    {d.peopleLosing} {d.peopleLosing === 1 ? 'person loses' : 'people lose'}
                                    {' '}{d.level.toLowerCase()} on {d.resourcesLost}
                                    {' '}{d.resourceType.toLowerCase()}{d.resourcesLost === 1 ? '' : 's'}
                                </div>
                            )}
                        </div>
                    ))}

                    {preview.capabilitiesGained.length > 0 && (
                        <div style={{ color: '#9f3a38', marginTop: '0.4em' }}>
                            <Icon name="arrow up" />
                            everyone holding this role gains:{' '}
                            {preview.capabilitiesGained.map((c: string) => c.toLowerCase().replace(/_/g, ' ')).join(', ')}
                        </div>
                    )}
                    {preview.capabilitiesLost.length > 0 && (
                        <div style={{ color: '#767676', marginTop: '0.4em' }}>
                            <Icon name="arrow down" />
                            they lose:{' '}
                            {preview.capabilitiesLost.map((c: string) => c.toLowerCase().replace(/_/g, ' ')).join(', ')}
                        </div>
                    )}

                    {/* Confirmation is required only for widening. Narrowing is visible to
                        whoever loses it and can be re-granted; widening is neither. The server
                        re-runs this check, so unticking it here is not a way round it. */}
                    {preview.widensAccess && (
                        <Message warning size="small" style={{ marginTop: '1em' }}>
                            <Checkbox
                                checked={confirmed}
                                label="I want to give these people this extra access"
                                onChange={(_, d) => setConfirmed(d.checked === true)}
                            />
                        </Message>
                    )}
                </Segment>
            )}

            <div style={{ marginTop: '1em' }}>
                {preview == null
                    ? (
                        <Button
                            primary
                            loading={busy}
                            disabled={busy || nothingChanged}
                            onClick={runPreview}
                        >
                            See what this changes
                        </Button>
                    )
                    : (
                        <Button
                            primary
                            loading={busy}
                            disabled={busy || (preview.widensAccess && !confirmed)}
                            onClick={apply}
                        >
                            Save
                        </Button>
                    )}
                <Button onClick={onCancel} disabled={busy}>Cancel</Button>
            </div>
        </Segment>
    );
};
